import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SvgIconsComponent } from '@zayan-erp/shared';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FilesystemService } from '../../services/file-system.service';

@Component({
  selector: 'app-move-folder',
  standalone: true,
  imports: [SvgIconsComponent, CommonModule, NgbTooltipModule],
  templateUrl: './move-folder.component.html',
  styleUrl: './move-folder.component.css'
})
export class MoveFolderComponent implements OnInit, OnDestroy {
  selectedDestination: any = null;
  folderHierarchy: any[] = [];
  folderPath: any[] = [];
  fileToMove: any = null;
  filesToMove: any[] = []; // For bulk move
  isBulkMove: boolean = false; // Flag to indicate if this is a bulk move
  nameConflictExists: boolean = false;
  ngUnsubscribe = new Subject<void>();

  #toaster = inject(ToastrService);
  #filesystemService = inject(FilesystemService);
  #activeModal = inject(NgbActiveModal);
  public activeModal = this.#activeModal;

  ngOnInit(): void {
    this.openMoveFolderModal();
  }

  navigateBack(): void {
    if (this.folderPath.length === 0) {
      // We're already at root, nothing to do
      return;
    }

    // Remove the current folder from the path
    this.folderPath.pop();

    // Get the parent folder (last item in folderPath) or null for root
    const parentFolder = this.folderPath.length > 0 ? this.folderPath[this.folderPath.length - 1] : null;

    // Update the selected destination to the parent
    this.selectedDestination = parentFolder;

    this.getFolders(parentFolder ? parentFolder.id : null);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  navigateFolder(folder: any): void {
    if (folder === null) {
      // Navigate to root
      this.folderPath = [];
      this.selectedDestination = null;
      this.getFolders(null);
    } else {
      // Find the index of the folder in the path
      const index = this.folderPath.findIndex(f => f.id === folder.id);

      if (index !== -1) {
        // If folder is in the path, navigate to it and remove subsequent folders
        this.folderPath = this.folderPath.slice(0, index + 1);
        // Update selected destination to the folder we navigated to
        this.selectedDestination = folder;
      } else {
        // If not in path already, update path and selected destination
        this.selectedDestination = folder;
        this.folderPath.push(folder);
      }

      this.getFolders(folder.id);
    }
  }

  selectFolder(folder: any): void {
    // If folder is null, it means we're selecting the root folder
    if (folder === null) {
      this.selectedDestination = null;
      this.folderPath = [];
      this.getFolders(null);
      return;
    }

    if (this.isBulkMove) {
      // For bulk move, check if any of the files would be moved into itself or its subfolder
      const folderItemsToMove = this.filesToMove.filter(item => item.type === 'Folder');
      for (const folderItem of folderItemsToMove) {
        if (folder.id === folderItem.id) {
          this.#toaster.error('Cannot move a folder into itself', 'Error!');
          return;
        }

        if (this.isChildFolder(folder, folderItem)) {
          this.#toaster.error('Cannot move a folder into its own subfolder', 'Error!');
          return;
        }
      }
    } else {
      // Single file move logic
      if (folder?.id! === this.fileToMove?.id!) {
        this.#toaster.error('Cannot move to the same folder', 'Error!');
        return;
      }

      // Check if this folder is a child of the folder being moved (for folders only)
      if (this.fileToMove?.type! === 'Folder' && this.isChildFolder(folder, this.fileToMove)) {
        this.#toaster.error('Cannot move a folder into its own subfolder', 'Error!');
        return;
      }
    }

    this.selectedDestination = folder;
    this.folderPath.push(folder);
    this.getFolders(folder?.id!);
  }

  isChildFolder(folder: any, sourceFolder: any = null): boolean {
    // If sourceFolder is not provided, use the fileToMove
    const source = sourceFolder || this.fileToMove;

    // Direct child check
    if (folder.parentId === source.id) {
      return true;
    }

    // Check if the potential destination is in the current folder path
    for (const pathFolder of this.folderPath) {
      if (pathFolder.id === source.id) {
        return true;
      }
    }

    return false;
  }

  openMoveFolderModal(): void {
    this.folderPath = [];
    this.selectedDestination = null;
    this.nameConflictExists = false;
    this.getFolders(null); // Start with root folders
  }

  getFolders(folderId: string | null): void {
    this.#filesystemService
      .listFoldersOnly(folderId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: data => {
          this.folderHierarchy = [];

          // Map folders to display format
          if (data?.directoryItems) {
            this.folderHierarchy = data.directoryItems;
            // Check for name conflicts after loading data
            this.nameConflictExists = this.checkForNameConflict();
          }
        }
      });
  }

  // Check if there's a file or folder with the same name in the destination
  checkForNameConflict(): boolean {
    if (this.isBulkMove) {
      if (this.filesToMove.length === 0) {
        return false;
      }

      // Check for name conflicts in bulk move
      const itemsToCheck = this.folderHierarchy;

      // For each file to move, check if there's a conflict
      return this.filesToMove.some(fileToMove =>
        itemsToCheck.some(
          item =>
            item.id !== fileToMove.id && // Skip the item being moved
            item.name.toLowerCase() === fileToMove.name.toLowerCase() // Case-insensitive name comparison
        )
      );
    } else {
      if (!this.fileToMove) {
        return false;
      }

      // Single file move name conflict check
      const itemsToCheck = this.folderHierarchy;

      // Search for name conflicts
      return itemsToCheck.some(
        item =>
          item.id !== this.fileToMove.id && // Skip the item being moved
          item.name.toLowerCase() === this.fileToMove.name.toLowerCase() // Case-insensitive name comparison
      );
    }
  }

  // Helper method to determine if a folder item should be disabled in the move modal
  isItemDisabled(item: any): boolean {
    if (item.type !== 'Folder') {
      return true; // Always disable non-folder items
    }

    if (this.isBulkMove) {
      // Check if this item is one of the items being moved
      return this.filesToMove.some(f => f.id === item.id);
    } else {
      // Single item move - disable if it's the item being moved
      return item.id === this.fileToMove?.id;
    }
  }

  // Helper method for the tooltip in the move modal
  getItemTooltip(item: any): string {
    if (item.type !== 'Folder') {
      return `Can't select ${item.name} because it is not a folder`;
    }

    if (this.isBulkMove) {
      return this.filesToMove.some(f => f.id === item.id) ? 'Cannot move item into itself' : '';
    } else {
      return item.id === this.fileToMove?.id ? "Can't move folder into itself" : '';
    }
  }

  selectFolderAndClose(): void {
    //if (this.selectedDestination) {
    this.activeModal.close({ id: this.selectedDestination?.id, name: this.selectedDestination?.name });
    //}
  }
}
